import React, { useState, useEffect, useCallback, useRef } from 'react';
import { feedAPI } from '../services/api';
import { RefreshCw, Play, ExternalLink, Clock, Newspaper, Video, X } from 'lucide-react';

// ── Video Modal ───────────────────────────────────────────────────────────────
function VideoModal({ video, onClose }) {
  const overlayRef = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (e.target === overlayRef.current) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div
      ref={overlayRef}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 300, padding: 16,
      }}
    >
      <div style={{ width: '100%', maxWidth: 800, position: 'relative' }}>
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: -40, right: 0,
            background: 'none', border: 'none', color: '#fff', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem',
          }}
        >
          <X size={16} /> Close
        </button>
        <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: 12, overflow: 'hidden' }}>
          <iframe
            src={`https://www.youtube.com/embed/${video.videoId}?autoplay=1&rel=0`}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
          />
        </div>
        <div style={{ marginTop: 10, color: '#fff', fontSize: '0.9rem', fontWeight: 500 }}>{video.title}</div>
        <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginTop: 3 }}>{video.channel}</div>
      </div>
    </div>
  );
}

// ── Highlight card ────────────────────────────────────────────────────────────
function HighlightCard({ video, onClick }) {
  return (
    <div
      onClick={() => onClick(video)}
      style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)', overflow: 'hidden', cursor: 'pointer',
        transition: 'border-color 0.15s, transform 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-hover)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      {/* Thumbnail */}
      <div style={{ position: 'relative', paddingBottom: '56.25%', background: '#000' }}>
        <img
          src={video.thumb}
          alt={video.title}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          onError={e => { e.target.style.opacity = 0.3; }}
        />
        {/* Play button overlay */}
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.2)', transition: 'background 0.15s',
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: '50%',
            background: 'rgba(255,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 12px rgba(0,0,0,0.5)',
          }}>
            <Play size={18} color="#fff" fill="#fff" style={{ marginLeft: 2 }} />
          </div>
        </div>
        {/* YouTube badge */}
        <div style={{
          position: 'absolute', bottom: 6, right: 6,
          background: 'rgba(0,0,0,0.75)', borderRadius: 4, padding: '2px 6px',
          fontSize: '0.65rem', color: '#fff', fontWeight: 600, letterSpacing: '0.3px',
        }}>
          YouTube
        </div>
      </div>
      {/* Info */}
      <div style={{ padding: '10px 12px' }}>
        <div style={{ fontSize: '0.82rem', fontWeight: 500, color: 'var(--text-primary)', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {video.title}
        </div>
        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 4 }}>{video.channel}</div>
      </div>
    </div>
  );
}

// ── News card ─────────────────────────────────────────────────────────────────
function NewsCard({ item }) {
  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: 'none', display: 'block' }}
    >
      <div
        className="card"
        style={{
          display: 'flex', gap: 12, alignItems: 'flex-start',
          transition: 'border-color 0.15s, transform 0.15s', cursor: 'pointer',
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-hover)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'translateY(0)'; }}
      >
        {/* Thumbnail if available */}
        {item.image && (
          <div style={{
            width: 80, height: 60, borderRadius: 6, overflow: 'hidden', flexShrink: 0,
            background: 'var(--bg-secondary)',
          }}>
            <img
              src={item.image}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={e => { e.target.parentElement.style.display = 'none'; }}
            />
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '0.88rem', fontWeight: 500, color: 'var(--text-primary)', lineHeight: 1.45, marginBottom: 5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {item.title}
          </div>
          {item.summary && (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 6, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {item.summary}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{
              fontSize: '0.68rem', fontWeight: 600, padding: '2px 8px', borderRadius: 20,
              background: 'var(--accent-dim)', color: 'var(--accent)',
            }}>
              {item.source}
            </span>
            {item.publishedAt && (
              <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 3 }}>
                <Clock size={10} /> {timeAgo(item.publishedAt)}
              </span>
            )}
            <ExternalLink size={11} color="var(--text-muted)" style={{ marginLeft: 'auto' }} />
          </div>
        </div>
      </div>
    </a>
  );
}

// ── Main Feed Page ────────────────────────────────────────────────────────────
export default function FeedPage() {
  const [news, setNews]             = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [hasYoutubeKey, setHasYoutubeKey] = useState(true);
  const [loading, setLoading]       = useState(true);
  const [tab, setTab]               = useState('news');
  const [activeVideo, setActiveVideo] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchFeed = useCallback(async () => {
    try {
      const r = await feedAPI.all();
      setNews(r.data.news || []);
      setHighlights(r.data.highlights || []);
      setHasYoutubeKey(r.data.hasYoutubeKey !== false);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Feed error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeed();
    // Auto-refresh news every 15 minutes (matches server cache)
    const interval = setInterval(fetchFeed, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchFeed]);

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div>
      {activeVideo && <VideoModal video={activeVideo} onClose={() => setActiveVideo(null)} />}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8, marginBottom: '1.25rem' }}>
        <div>
          <h1 className="page-title">Cricket Feed</h1>
          <p className="page-subtitle">
            Live news, IPL gossip &amp; match highlights
            {lastRefresh && (
              <span style={{ marginLeft: 8, color: 'var(--text-muted)', fontSize: '0.73rem' }}>
                · Refreshed {lastRefresh.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </p>
        </div>
        <button
          className="btn btn-outline"
          onClick={() => { setLoading(true); fetchFeed(); }}
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem' }}
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: '1.25rem' }}>
        <button
          className={`filter-chip${tab === 'news' ? ' active' : ''}`}
          onClick={() => setTab('news')}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <Newspaper size={13} /> News ({news.length})
        </button>
        <button
          className={`filter-chip${tab === 'highlights' ? ' active' : ''}`}
          onClick={() => setTab('highlights')}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <Video size={13} /> Highlights ({highlights.length})
        </button>
      </div>

      {/* News tab */}
      {tab === 'news' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {news.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
              <Newspaper size={40} style={{ opacity: 0.25, marginBottom: 12 }} />
              <p style={{ marginBottom: 8 }}>No news loaded yet.</p>
              <p style={{ fontSize: '0.82rem' }}>
                Add <code style={{ background: 'var(--bg-secondary)', padding: '1px 6px', borderRadius: 4 }}>NEWS_API_KEY</code> to your backend <code style={{ background: 'var(--bg-secondary)', padding: '1px 6px', borderRadius: 4 }}>.env</code> for live articles.
              </p>
              <a href="https://newsapi.org" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontSize: '0.82rem', marginTop: 8, display: 'inline-block' }}>
                Get free key at newsapi.org →
              </a>
            </div>
          ) : (
            news.map(item => <NewsCard key={item.id} item={item} />)
          )}
        </div>
      )}

      {/* Highlights tab */}
      {tab === 'highlights' && (
        <>
          {!hasYoutubeKey && (
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Video size={12} />
              Add <code style={{ background: 'var(--bg-secondary)', padding: '1px 5px', borderRadius: 3 }}>YOUTUBE_API_KEY</code> to .env for live highlights.
            </div>
          )}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 14,
          }}>
            {highlights.map(video => (
              <HighlightCard key={video.id} video={video} onClick={setActiveVideo} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
